import { Component, Element, Event, EventEmitter, h, Method, Prop, Watch } from '@stencil/core';

import { ClassNames, LhClickArrowTop, LhClickBrandLogo, LhClickLink } from '../../../utils/interfaces';

const arrowTop = (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20.6875 8.75L15.4844 16.1562L19.8906 16.1562L19.8906 31.25L21.4609 31.25L21.4609 16.1562L25.8437 16.1562L20.6875 8.75Z" fill="white" />
    <rect x="0.5" y="39.5" width="39" height="39" transform="rotate(-90 0.5 39.5)" stroke="white" />
  </svg>
);

const logo = (
  <svg version="1.1" id="Brand" xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" viewBox="0 0 1000 124">
    <path
      d="M238.9,95.2h31.4V124h-89.9V0h89v28.8h-30.6v19.8h27.9v26.1h-27.9V95.2z M101.3,124h59.3V0h-59.3V124z M58.4,0H0l0,124h85.4
            V90.8h-27V0z M654.1,74.6H682V48.5h-27.9V28.8h30.6V0h-89v124h89.9V95.2h-31.5V74.6z M423.4,90.7c0,16.2-9.5,26.8-25.7,30.7
            c-7.1,1.7-18.6,2.6-29,2.6h-80.5V0h80c10.2,0,21.4,0.9,28.4,2.6c15.8,3.9,25.1,14.5,25.1,30.7c0,14.9-8.8,25.1-20,28.4
            C413.7,64.4,423.4,75,423.4,90.7 M344.7,50.3h8.1c10.8,0,14.4-5.4,14.4-13s-3.6-13-14.4-13h-8.1V50.3z M369,86.3
            c0-8.1-3.7-13.5-15.3-13.5h-9v27h9C365.4,99.7,369,94.3,369,86.3 M517,45.8h-19.8V0h-58.4v124h58.4V76.4H517V124h58.4V0H517V45.8z
            M967.9,72.3c15.3-7,22.1-18.6,22.1-34.4c0-18.4-11-30.4-29.4-34.9c-8.1-1.9-21.2-3-33-3h-71.9v124h55.7V84.5h5l21.1,39.5h62.5
            L967.9,72.3z M919.5,58.4h-8.1V28.8h8.1c10.8,0,14.4,5.4,14.4,14.8C933.9,53,930.3,58.4,919.5,58.4z M815.4,72.3
            c15.3-7,22.1-18.6,22.1-34.4c0-18.4-11-30.4-29.4-34.9c-8.1-1.9-21.2-3-33-3h-71.9v124h55.7V84.5h5l21.1,39.5h62.5L815.4,72.3z
            M766.9,58.4h-8.1V28.8h8.1c10.8,0,14.4,5.4,14.4,14.8C781.3,53,777.7,58.4,766.9,58.4z"
      fill="#FFFFFF"
    />
  </svg>
);

export interface LinksValue {
  title: string;
  items: LinksValueItem[];
}
export interface LinksValueItem {
  label: string;
  to: string;
  itemId: number | string;
  target: '_blank' | '_self' | '_parent' | '_top';
}

export interface AdditionalLinksValue {
  label: string;
  to: string;
  itemId: number | string;
  target: '_blank' | '_self' | '_parent' | '_top';
}

@Component({
  tag: 'patternlib-footer',
  styleUrl: 'patternlib-footer.scss',
  shadow: true,
})
export class PatternlibFooter {
  private _links: LinksValue[] = [];
  private _additionalLinks: AdditionalLinksValue[] = [];

  @Element() hostElement: HTMLPatternlibFooterElement;

  /**
   * Pass an array to the footer, which contains title and items
   */
  @Prop() links: LinksValue[] | string = '';

  /**
   * Pass an array to the footer, which contains label and url
   */
  @Prop() additionalLinks: AdditionalLinksValue[] | string = '';

  /**
   *  Define to show or hidden the arrow to top icon on the footer
   */
  @Prop() showArrowIcon = false;

  /**
   * Provide a method to be triggered each time the arrow-top icon is clicked.
   **/
  @Event({ eventName: 'lhClickArrowTop' }) clickArrowTop: EventEmitter<LhClickArrowTop>;
  /**
   * Provide a method to be triggered each time a link is clicked.
   */
  @Event({ eventName: 'lhClickLink' }) clickLink: EventEmitter<LhClickLink>;
  /**
   * Provide a method to be triggered each time the brand-logo is clicked.
   */
  @Event({ eventName: 'lhClickBrandLogo' }) clickBrandLogo: EventEmitter<LhClickBrandLogo>;

  @Watch('links')
  arrayLinksWatcher(newValue: LinksValue[] | string): void {
    if (typeof newValue === 'string' && newValue != '') {
      this._links = JSON.parse(newValue);
    } else if (Array.isArray(newValue)) {
      this._links = newValue;
    } else {
      this._links = [];
    }
  }

  @Watch('additionalLinks')
  arrayAdditionalLinksWatcher(newValue: AdditionalLinksValue[] | string): void {
    if (typeof newValue === 'string' && newValue != '') {
      this._additionalLinks = JSON.parse(newValue);
    } else if (Array.isArray(newValue)) {
      this._additionalLinks = newValue;
    } else {
      this._additionalLinks = [];
    }
  }

  /**
   * Set the prop screenSize from outside (e.g. ResizeObserver)
   * @param {ScreenSize['screen']} screen - The size of the Screen
   * @deprecated
   */
  @Method()
  async setScreenSize(_: any): Promise<void> {
    return;
  }

  componentWillLoad(): void {
    this.arrayLinksWatcher(this.links);
    this.arrayAdditionalLinksWatcher(this.additionalLinks);
  }

  private openLink = (itemId: number | string) => () => {
    if (itemId !== null) {
      this.clickLink.emit({ itemId: itemId });
    }
  };

  private handleArrowTopClick = () => {
    this.clickArrowTop.emit();
  };

  private handleBrandLogoClick = () => {
    this.clickBrandLogo.emit();
  };

  private ifNoLinks = (): boolean => {
    return this.links == null || this.links === '' || (Array.isArray(this.links) && (this.links as LinksValue[]).length === 0);
  };

  render(): any {
    const footerClass: ClassNames = {
      footer: true,
    };

    const footerContainerClass: ClassNames = {
      'footer-container': true,
    };

    const footerWrapperClass: ClassNames = {
      'footer-wrapper': true,
    };

    const footerSectionClass: ClassNames = {
      'footer-section': true,
    };

    const footerSectionTitleClass: ClassNames = {
      'footer-section-title': true,
    };

    const footerSectionItemClass: ClassNames = {
      'footer-section-item': true,
    };

    const footerBottomClass: ClassNames = {
      'footer-bottom': true,
    };

    const liebherrIconClass: ClassNames = {
      'footer-liebherr-icon': true,
      'area-two': this.ifNoLinks(),
      'w-[163px] sm:w-[124px] box-content': true,
    };

    const arrowIconClass: ClassNames = {
      'footer-arrow-icon': true,
      'pr-[24px]': this.ifNoLinks() && this.showArrowIcon,
      'area-three': this.ifNoLinks(),
    };

    const additionalLinksClass: ClassNames = {
      'hidden lg:block footer-additional-link': true,
    };

    const footerPortalClass: ClassNames = {
      'footer-portal grid tablet:flex md:pt-0': true,
      'pt-[60px]': this.ifNoLinks(),
    };

    const footerPortalTextClass: ClassNames = { 'flex flex-col tablet:flex-row footer-portal-text': true };

    const logoInFooter = (
      <div class={liebherrIconClass} onClick={this.handleBrandLogoClick}>
        <slot>{logo}</slot>
      </div>
    );

    const footerPortal = (
      <div class={footerPortalClass}>
        {logoInFooter}
        <div class={footerPortalTextClass}>
          {this._additionalLinks.map(item => {
            return (
              <div>
                <a href={item.to} target={item.target ?? '_blank'} onClick={this.openLink(item.itemId)}>
                  {item.label}
                </a>
              </div>
            );
          })}
        </div>
        <div class={arrowIconClass} onClick={this.handleArrowTopClick}>
          {this.showArrowIcon ? arrowTop : ''}
        </div>
      </div>
    );

    const footerBottom = (
      <div class={footerBottomClass}>
        {logoInFooter}

        <div class={additionalLinksClass}>
          {this._additionalLinks.map(item => {
            return (
              <div class={footerSectionItemClass}>
                <a href={item.to} target={item.target ?? '_blank'} onClick={this.openLink(item.itemId)}>
                  {item.label}
                </a>
              </div>
            );
          })}
        </div>
        <div class={arrowIconClass} onClick={this.handleArrowTopClick}>
          {this.showArrowIcon ? arrowTop : ''}
        </div>
      </div>
    );

    const footerDefault = (
      <div class={footerContainerClass}>
        <div class={footerWrapperClass}>
          {this._links.map(sectionItem => {
            return (
              <div class={footerSectionClass}>
                <div class={footerSectionTitleClass}>{sectionItem.title}</div>
                {sectionItem.items.map(item => {
                  return (
                    <div class={footerSectionItemClass}>
                      <a href={item.to} target={item.target ?? '_blank'} onClick={this.openLink(item.itemId)}>
                        {item.label}
                      </a>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div class={additionalLinksClass}>
          {this._additionalLinks.map(item => {
            return (
              <div class={footerSectionItemClass}>
                <a href={item.to} target={item.target ?? '_blank'} onClick={this.openLink(item.itemId)}>
                  {item.label}
                </a>
              </div>
            );
          })}
        </div>
        {footerBottom}
      </div>
    );

    return <footer class={footerClass}>{this.ifNoLinks() ? footerPortal : footerDefault}</footer>;
  }
}
